import axiosInstance from '../../../utils/axios';

/**
 * Satellite Insights: the Support endpoints (backend Routes/SuperAdmin/UserData.js).
 *
 * The same handlers the farmer app calls under /user, finding any farm that is
 * not deleted instead of one the caller owns, so the answers have exactly the
 * farmer app's shape. "No picture today" and "too cloudy" are 200s with a
 * `status`, not errors; only a genuine failure throws.
 */

/** True when the endpoint simply is not deployed yet (so the page falls back quietly). */
const isMissingEndpoint = (err) => {
  const s = err?.response?.status;
  return s === 404 || s === 405 || s === 501;
};

const get = async (url, params) => {
  try {
    const { data } = await axiosInstance.get(url, { params });
    return data?.data || null;
  } catch (err) {
    if (isMissingEndpoint(err)) return null;
    throw err;
  }
};

/** Today's picture and legend of one index: 'ndvi', 'ndmi', 'ndre' or 'ndwi'. */
export const getSatelliteIndex = (farmId, index, params = {}) =>
  get(`/super-admin/farm/${encodeURIComponent(farmId)}/satellite/${index}`, params);

/** One index over past days: `{ index, days }` or `{ index, start, end }`. */
export const getSatelliteHistory = (farmId, params = {}) =>
  get(`/super-admin/farm/${encodeURIComponent(farmId)}/satellite/history`, params);

/**
 * The farm's crop plan (crop, variety, sowing date and status), or null when it
 * has none — or crop plans are not enabled yet (501).
 */
export const getCropPlan = async (farmId) => {
  try {
    const { data } = await axiosInstance.get(`/super-admin/farm/${encodeURIComponent(farmId)}/crop-plan`);
    return data?.plan ?? null;
  } catch (err) {
    if (isMissingEndpoint(err)) return null;
    throw err;
  }
};

/** The Support farm list, searched by keyword (used to open a farm from a direct link). */
export const findFarmRow = async (farmId) => {
  const { data } = await axiosInstance.get('/super-admin/farms', { params: { page: 1, limit: 15, keyword: farmId } });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return rows.find((row) => String(row.farm_id) === String(farmId)) || null;
};
